import {
  Card,
  Typography,
  Spin,
  Row,
  Col,
  message,
  Button,
  Tooltip,
} from "antd";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { TeamOutlined, CopyOutlined } from "@ant-design/icons";
import SubscriptionButton from "./SubscriptionButton";
import { searchForGroupExternal } from "../../actions/api";
import { DEFAULT_GROUP_TABLE_QUERY_FIELDS } from "../../constants";
import { useParams, useNavigate } from "react-router-dom";
import { types as at, errorAction } from "@authzsvc/api-lib";
import { showErrorNotification } from "../../util";

const { Text } = Typography;

const GroupDetailsExternal = () => {
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const externalGroupSearch = useSelector(
    (state) => state.groups.groupSearch?.[0]
  );
  const { id } = useParams();
  const dispatch = useDispatch();

  const groupTableQueryFields = [
    ...DEFAULT_GROUP_TABLE_QUERY_FIELDS,
    "selfSubscriptionType",
    "approvalRequired",
    "administratorsId",
  ];

  const init = async () => {
    setLoading(true);
    const result = await dispatch(
      searchForGroupExternal({
        filter: `groupIdentifier:Eq:${id}`,
        sort: "groupIdentifier",
        limit: 1,
        field: groupTableQueryFields,
      })
    );
    if (
      result?.payload?.data?.length === 0 ||
      result?.type === errorAction(at.SEARCH_FOR_GROUPS_EXTERNAL)
    ) {
      showErrorNotification(
        result?.payload?.title || "Failed to find group",
        result?.payload?.data?.message || "Sorry, something went wrong"
      );

      navigate(`/`);
    }
    setLoading(false);
  };

  const handleCopySubscriptionLink = () => {
    const currentUrl = window.location.href;
    const identifier = externalGroupSearch.groupIdentifier;
    const baseGroupUrl = currentUrl.replace(/\/details$/, "");
    const subscriptionLink = baseGroupUrl.replace(
      /\/group\/[^/]+$/,
      `/group/${identifier}/subscribe`
    );

    navigator.clipboard
      .writeText(subscriptionLink)
      .then(() => {
        message.success("Subscription link copied");
      })
      .catch(() => {
        message.error("Failed to copy link");
      });
  };

  const getTooltipMessage = () => {
    if (!externalGroupSearch) return "";
    if (externalGroupSearch.approvalRequired) {
      return "Request to join this group";
    }
    if (externalGroupSearch.selfSubscriptionType === "Closed") {
      return "This group does not allow self subscription.";
    }
    if (
      externalGroupSearch.syncType !== "NoSync" &&
      externalGroupSearch.syncType !== "Master"
    ) {
      return "This group is managed via E-groups, please visit e-groups.cern.ch to subscribe";
    }
    return "Modify group subscription";
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <Spin spinning={loading}>
      {!loading && (
        <>
          <Card
            title={
              <Row align="middle">
                <Col flex="32px" style={{ marginRight: 16 }}>
                  <TeamOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />
                </Col>
                <Col flex="auto">
                  <Typography.Title level={4} style={{ marginTop: 0 }}>
                    <Typography.Text
                      type="secondary"
                      style={{ marginRight: 8, fontSize: "inherit" }}
                    >
                      Group:
                    </Typography.Text>
                    {externalGroupSearch.groupIdentifier}
                    <Tooltip title="Copy subscription link">
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={handleCopySubscriptionLink}
                      />
                    </Tooltip>
                  </Typography.Title>
                </Col>
                <Col flex="none" style={{ textAlign: "right" }}>
                  <SubscriptionButton
                    group={externalGroupSearch}
                    disabled={false}
                    tooltipMessage={getTooltipMessage()}
                  />
                </Col>
              </Row>
            }
            style={{ marginBottom: 24 }}
          >
            <p>
              <Text strong>Group Name:</Text>{" "}
              {externalGroupSearch.groupIdentifier}
            </p>
            <p>
              <Text strong>Description:</Text> {externalGroupSearch.description}
            </p>
            <p>
              <Text strong>Self Subscription Type:</Text>{" "}
              {externalGroupSearch.selfSubscriptionType}
            </p>
            <p>
              <Text strong>Approval Required:</Text>{" "}
              {externalGroupSearch.approvalRequired ? "Yes" : "No"}
            </p>
          </Card>
        </>
      )}
    </Spin>
  );
};
export default GroupDetailsExternal;
